import React, { useEffect, useState } from 'react';
import { sp } from './sponsorApi';
import { HandCoins, Handshake, Plus, Loader2 } from 'lucide-react';

const TYPES = [
  { v: 'FINANCIAL', l: 'Financier' },
  { v: 'IN_KIND', l: 'Nature' },
  { v: 'SKILLS', l: 'Compétences' },
  { v: 'MEDIA', l: 'Média / Communication' },
];
const STATUS = ['PLANNED', 'IN_PROGRESS', 'DELIVERED', 'CANCELLED'];
const STATUS_LABEL = { PLANNED: 'Prévue', IN_PROGRESS: 'En cours', DELIVERED: 'Réalisée', CANCELLED: 'Annulée' };

export default function ContributionsTab() {
  const [data, setData] = useState({ contributions: [], total_financial: 0, total_in_kind_valued: 0 });
  const [cparts, setCparts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ type: 'FINANCIAL', label: '', amount: '', estimated_value: '', description: '' });
  const [cpForm, setCpForm] = useState({ label: '', description: '', type: 'VISIBILITY' });
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, cp] = await Promise.all([sp.get('/contributions'), sp.get('/contreparties')]);
      setData(c.data); setCparts(cp.data.contreparties || []);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addContribution = async () => {
    setMsg('');
    try {
      await sp.post('/contributions', {
        type: form.type, label: form.label, description: form.description,
        amount: form.type === 'FINANCIAL' ? parseFloat(form.amount) || 0 : null,
        estimated_value: form.type !== 'FINANCIAL' ? parseFloat(form.estimated_value) || 0 : null,
      });
      setForm({ type: 'FINANCIAL', label: '', amount: '', estimated_value: '', description: '' });
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };

  const setStatus = async (id, status) => { await sp.patch(`/contributions/${id}/statut`, { status }); await load(); };

  const addCounterpart = async () => {
    if (!cpForm.label) return;
    await sp.post('/contreparties', cpForm);
    setCpForm({ label: '', description: '', type: 'VISIBILITY' });
    await load();
  };
  const setExecution = async (id, rate) => { await sp.patch(`/contreparties/${id}/execution`, { execution_rate: rate }); await load(); };

  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="contributions-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-800 rounded-xl p-5" data-testid="total-financial">
          <p className="text-slate-400 text-sm">Total financier</p>
          <p className="text-2xl font-bold text-white">{(data.total_financial || 0).toLocaleString('fr-FR')} €</p>
        </div>
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-800 rounded-xl p-5" data-testid="total-inkind">
          <p className="text-slate-400 text-sm">Valorisation nature/compétences</p>
          <p className="text-2xl font-bold text-white">{(data.total_in_kind_valued || 0).toLocaleString('fr-FR')} €</p>
        </div>
      </div>

      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 text-emerald-400 mb-4"><HandCoins className="w-5 h-5" /><h2 className="text-white font-semibold">Contributions</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="contrib-type-select" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Intitulé" data-testid="contrib-label-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          {form.type === 'FINANCIAL' ? (
            <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Montant €" data-testid="contrib-amount-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          ) : (
            <input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} placeholder="Valeur estimée €" data-testid="contrib-value-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          )}
          <button onClick={addContribution} disabled={!form.label} data-testid="contrib-add-btn" className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center justify-center gap-1"><Plus className="w-4 h-4" />Ajouter</button>
        </div>
        {msg && <p className="text-sm text-amber-300 mb-2" data-testid="contrib-msg">{msg}</p>}
        <ul className="space-y-2">
          {(data.contributions || []).length === 0 && <li className="text-slate-500 text-sm" data-testid="contrib-empty">Aucune contribution.</li>}
          {(data.contributions || []).map((c) => (
            <li key={c.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 text-sm" data-testid={`contrib-${c.id}`}>
              <div>
                <span className="text-white">{c.label}</span>
                <span className="text-slate-500 ml-2 text-xs">{TYPES.find((t) => t.v === c.type)?.l}{c.type === 'FINANCIAL' ? ` · ${c.amount || 0} €` : (c.estimated_value ? ` · ${c.estimated_value} €` : '')}</span>
              </div>
              <select value={c.status} onChange={(e) => setStatus(c.id, e.target.value)} data-testid={`contrib-status-${c.id}`} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200">
                {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 text-emerald-400 mb-4"><Handshake className="w-5 h-5" /><h2 className="text-white font-semibold">Contreparties de visibilité</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <input value={cpForm.label} onChange={(e) => setCpForm({ ...cpForm, label: e.target.value })} placeholder="Contrepartie (ex: logo site)" data-testid="cp-label-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <input value={cpForm.description} onChange={(e) => setCpForm({ ...cpForm, description: e.target.value })} placeholder="Détail" data-testid="cp-desc-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <button onClick={addCounterpart} disabled={!cpForm.label} data-testid="cp-add-btn" className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center justify-center gap-1"><Plus className="w-4 h-4" />Ajouter</button>
        </div>
        <ul className="space-y-2">
          {cparts.length === 0 && <li className="text-slate-500 text-sm" data-testid="cp-empty">Aucune contrepartie.</li>}
          {cparts.map((c) => (
            <li key={c.id} className="bg-slate-800/60 rounded-lg px-3 py-3 text-sm" data-testid={`cp-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white">{c.label} <span className="text-slate-500 text-xs">· {c.status}</span></span>
                <span className="text-emerald-300 font-semibold">{c.execution_rate}%</span>
              </div>
              <input type="range" min="0" max="100" step="10" value={c.execution_rate} onChange={(e) => setExecution(c.id, parseInt(e.target.value, 10))} data-testid={`cp-exec-${c.id}`} className="w-full accent-emerald-500" />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
